import React, { useMemo } from "react";
import { useGetBuildingsWithSwimmers } from "../../../swimmers/services/swimmerService";
import { useGetBuildingsWithNews } from "../../../news/services/newService";
import { ReportGenerator } from "../../components/ReportGenerator/ReportGenerator";
import { useSwimmersReportHandler } from "../../hooks/useSwimmersReportHandler";
import { useNewsReportHandler } from "../../hooks/useNewsReportHandler";
import { useConsolidatedReportHandler } from "../../hooks/useConsolidatedReportHandler";
import { useGetBuildings } from "../../../buildings/services/buildingService";
import { useManagementReportHandler } from "../../hooks/useManagementReportHandler";
import { useManagementReportNewHandler } from "../../hooks/useManagementReportNewHandler";
import { useManagementYearSummaryReportHandler } from "../../hooks/useManagementYearSummaryReportHandler";
import {
  REPORTS_PAGE_INTRO,
  REPORTS_PAGE_GLOBAL_NOTE,
  NOTE_LABEL,
  MANAGEMENT_NOTE, //TODO: Restore in the future
  MANAGEMENT_NEW_NOTE,
  getExcelPdfReportNote,
  NO_BUILDINGS_WITH_SWIMMERS_IN_RANGE,
  NO_BUILDINGS_WITH_NEWS_IN_RANGE,
} from "../../constants/reportMessages";
import { ManagementReportGenerator } from "../../components/ManagementReportGenerator/ManagementReportGenerator";
import { ManagementYearSummaryReportGenerator } from "../../components/ManagementYearSummaryReportGenerator/ManagementYearSummaryReportGenerator";

export const ReportsPage: React.FC = () => {
  const handleGenerateSwimmersReport = useSwimmersReportHandler();
  const buildingsWithSwimmersFetcher = useGetBuildingsWithSwimmers();

  const handleGenerateNewsReport = useNewsReportHandler();
  const buildingsWithNewsFetcher = useGetBuildingsWithNews();
  const handleGenerateConsolidatedReport = useConsolidatedReportHandler();

  // TODO: Restore when ManagementReportGenerator is re-enabled
  const handleGenerateManagementReport = useManagementReportHandler();
  const handleGenerateManagementReportNew = useManagementReportNewHandler();
  const handleGenerateManagementYearSummaryReport =
    useManagementYearSummaryReportHandler();
  const { data: buildingsData, isLoading: isFetchingBuildings } =
    useGetBuildings({ page_size: 1000, status: "ACTIVE" });
  const managementBuildingOptions = useMemo(
    () => (buildingsData?.items ?? []).map((b) => ({ id: b.id, name: b.name })),
    [buildingsData],
  );

  return (
    <div className="flex flex-col gap-6 px-3 sm:px-6 py-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-lifeguards-blue">
          Reportes
        </h1>
        <p className="text-gray-600 mt-2">{REPORTS_PAGE_INTRO}</p>
        <div className="mt-4 p-4 bg-blue-50 rounded-md">
          <p className="text-sm text-gray-700">
            <strong>{NOTE_LABEL}:</strong> {REPORTS_PAGE_GLOBAL_NOTE}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <ManagementReportGenerator
          title="Informe de Gestión"
          description={MANAGEMENT_NOTE}
          onGenerate={handleGenerateManagementReport}
          buildingOptions={managementBuildingOptions}
          isFetchingBuildings={isFetchingBuildings}
        />

        <ManagementReportGenerator
          title="Informe de Gestión"
          description={MANAGEMENT_NEW_NOTE}
          onGenerate={handleGenerateManagementReportNew}
          buildingOptions={managementBuildingOptions}
          isFetchingBuildings={isFetchingBuildings}
          badge="Beta"
        />

        <ManagementYearSummaryReportGenerator
          title="Resumen Anual del Informe de Gestión"
          description="Genera un PDF de 3 páginas con portada, resumen anual del año seleccionado y página de cierre."
          onGenerate={handleGenerateManagementYearSummaryReport}
          buildingOptions={managementBuildingOptions}
          isFetchingBuildings={isFetchingBuildings}
        />

        <ReportGenerator
          title="Reporte Consolidado"
          description="Consolidado de novedades por salvavidas y por unidad para todas las copropiedades activas. Permite períodos de hasta 366 días."
          filenamePrefix="consolidado"
          reportType="consolidated"
          onGenerate={handleGenerateConsolidatedReport}
          showFormatSelector={false}
          maxRangeDays={366}
        />

        <ReportGenerator
          title="Reporte de Bañistas"
          description={getExcelPdfReportNote("bañistas")}
          filenamePrefix="banistas"
          reportType="swimmers"
          onGenerate={handleGenerateSwimmersReport}
          buildingsFetcher={buildingsWithSwimmersFetcher}
          showBuildingFilter={true}
          emptyBuildingsMessage={NO_BUILDINGS_WITH_SWIMMERS_IN_RANGE}
        />

        <ReportGenerator
          title="Reporte de Novedades"
          description={getExcelPdfReportNote("novedades")}
          filenamePrefix="novedades"
          reportType="news"
          onGenerate={handleGenerateNewsReport}
          buildingsFetcher={buildingsWithNewsFetcher}
          showBuildingFilter={true}
          showReportTypeFilter={true}
          emptyBuildingsMessage={NO_BUILDINGS_WITH_NEWS_IN_RANGE}
        />
      </div>
    </div>
  );
};
